import { BriefcaseBusiness, Contact2, Phone } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { Switch } from "../../../../../components/ui/switch";
import { formatCnpj } from "../../../utils/format-cnpj";
import { cleanCnpj } from "../../../../../utils/format-utils";
import { formatCpf } from "../../../utils/format-cpf";
import {
  CadastroDocument,
  CadastroEditFormData,
  GATEWAYS_PAGAMENTO,
} from "../types";
import { MissingFieldsAlert } from "../../../components/missing-fields-alert";
import {
  EDIT_REQUIRED_FIELDS,
  getMissingRequiredFields,
} from "../../../utils/required-fields";

interface EditDialogProps {
  open: boolean;
  document: CadastroDocument | null;
  formData: CadastroEditFormData;
  allOps: string[];
  onFormChange: (data: CadastroEditFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function EditDialog({
  open,
  document,
  formData,
  allOps,
  onFormChange,
  onSave,
  onCancel,
  isSaving,
}: EditDialogProps) {
  const formatPhone = (phone?: string) => {
    if (!phone) return "";
    const numbersOnly = phone.replace(/\D/g, "");
    if (numbersOnly.startsWith("55") && numbersOnly.length <= 13) {
      return numbersOnly
        .replace(/^(\d{2})(\d{2})(\d)/, "+$1 ($2) $3")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return phone;
  };

  const handleCnpjChange = (value: string) => {
    onFormChange({ ...formData, cnpj: cleanCnpj(value).slice(0, 14) });
  };

  const formatDateInput = (value: string) => {
    const numbersOnly = value.replace(/[^\d-]/g, "");

    if (numbersOnly.length <= 4) {
      return numbersOnly;
    } else if (numbersOnly.length <= 7) {
      return numbersOnly.replace(/^(\d{4})(\d)/, "$1-$2");
    } else {
      return numbersOnly
        .replace(/^(\d{4})-(\d{2})(\d)/, "$1-$2-$3")
        .substr(0, 10);
    }
  };

  const handleDateChange = (value: string) => {
    const formatted = formatDateInput(value);
    onFormChange({ ...formData, data_nascimento: formatted });
  };

  // formData é semeado a partir do documento, então reflete tanto o que já estava
  // gravado quanto o que o operador acabou de digitar.
  const missingFields = getMissingRequiredFields(
    formData,
    EDIT_REQUIRED_FIELDS,
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar Cadastro</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <MissingFieldsAlert
            missingFields={missingFields}
            description="Preencha os campos abaixo ou busque os dados com o motorista. O cadastro pode ser salvo assim mesmo, mas o campo seguirá vazio no sistema."
          />

          <div className="space-y-2">
            <Label>Nome Completo (Read-only)</Label>
            <Input value={document?.nome_completo || ""} disabled />
          </div>

          <div className="p-3 rounded-md border">
            <div className="flex items-center gap-2">
              {document?.nao_aprovado_azumi === true ? (
                <span className="text-sm font-semibold text-red-600">
                  ❌ Não Cadastrado no Azumi
                </span>
              ) : document?.nao_aprovado_azumi === false ? (
                <span className="text-sm font-semibold text-green-600">
                  ✅ Cadastrado no Azumi
                </span>
              ) : (
                <span className="text-sm font-semibold text-orange-600">
                  ❓ Status Indefinido no Azumi
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CPF (Read-only)</Label>
              <div className="relative">
                <Contact2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={formatCpf(document?.cpf)}
                  disabled
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Telefone (Read-only)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  value={formatPhone(document?.telefone)}
                  disabled
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-4">Campos Editáveis</h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome_completo">Nome Completo</Label>
                <Input
                  id="nome_completo"
                  value={formData.nome_completo}
                  onChange={(e) =>
                    onFormChange({ ...formData, nome_completo: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data Nascimento</Label>
                  <Input
                    id="data_nascimento"
                    placeholder="YYYY-MM-DD"
                    value={formData.data_nascimento}
                    onChange={(e) => handleDateChange(e.target.value)}
                    maxLength={10}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <div className="relative">
                    <BriefcaseBusiness className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="cnpj"
                      placeholder="XX.XXX.XXX/XXXX-XX"
                      value={formatCnpj(formData.cnpj)}
                      onChange={(e) => handleCnpjChange(e.target.value)}
                      maxLength={18}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ops">OPS</Label>
                <Select
                  value={formData.ops}
                  onValueChange={(value) =>
                    onFormChange({ ...formData, ops: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma OPS" />
                  </SelectTrigger>
                  <SelectContent>
                    {allOps.map((ops) => (
                      <SelectItem key={ops} value={ops}>
                        {ops}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="gateway_pagamento">Gateway de Pagamento</Label>
                <Select
                  value={formData.gateway_pagamento}
                  onValueChange={(value) =>
                    onFormChange({ ...formData, gateway_pagamento: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o gateway" />
                  </SelectTrigger>
                  <SelectContent>
                    {GATEWAYS_PAGAMENTO.map((gateway) => (
                      <SelectItem key={gateway} value={gateway}>
                        {gateway === "fitbank" ? "FitBank" : "Transfeera"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ifood_uuid">iFood UUID</Label>
                <Input
                  id="ifood_uuid"
                  value={formData.ifood_uuid}
                  onChange={(e) =>
                    onFormChange({ ...formData, ifood_uuid: e.target.value })
                  }
                />
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="ativo">Usuário Ativo</Label>
                  <p className="text-sm text-muted-foreground">
                    Desativar bloqueia o usuário na operação
                  </p>
                </div>
                <Switch
                  id="ativo"
                  checked={formData.ativo}
                  onCheckedChange={(checked) =>
                    onFormChange({ ...formData, ativo: checked })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
